import { useState, useMemo } from 'react';
import { CURRENT_USER, getAuthHeaders } from '../globals.js';
import { loadErrorOf } from '../dataLoad.js';
import { ErrorState } from './ui/States.jsx';
import { FORM_TYPES, requestKindOf, topicName, requestIssuesOf, ISSUE_LABELS } from '../requestForms.js';
import { OriginTag, RequestOriginPanel, RequestPersonPanel, RequestOpportunityPanel, RequestLinks } from './requests/RequestOrigin.jsx';

const STATUS_OPTIONS = ['New', 'In review', 'Invited to call', 'Closed'];
// The stored values stay in English; this is what the screen shows
const REQUEST_STATUS_ES = { 'New': 'Nueva', 'In review': 'En revisión', 'Invited to call': 'Invitada a una llamada', 'Closed': 'Cerrada' };

const STATUS_COLORS = {
  'New': '#2D5F99',
  'In review': '#9A6212',
  'Invited to call': '#10b981',
  'Closed': '#6b7280',
};

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es', { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function RequestsHub({ data, api, onAddRecord, onUpdateRecord, onDeleteRecord, onReload }) {
  const [tab, setTab] = useState('requests');
  const [selected, setSelected] = useState(null);
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterLang, setFilterLang] = useState('All');
  const [filterType, setFilterType] = useState('All');
  const [filterTopic, setFilterTopic] = useState('All');
  const topics = data?.requestTopics || [];
  const events = data?.events || [];
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null); // { tone: 'ok' | 'error', text }
  const [deleteError, setDeleteError] = useState('');
  // The requests could not be read: never shown as "no requests"
  const loadError = loadErrorOf(data, 'diagnosticIntakes');

  const intakes = useMemo(() => {
    return (data?.diagnosticIntakes || [])
      .filter(r => {
        const status = r.fields?.['Status'];
        // Show only public form submissions (no Token = came from public form, or Status = Submitted from public)
        // We identify public-form records by absence of Token field
        return status === 'Submitted' && !r.fields?.['Token'];
      })
      .sort((a, b) => {
        const da = a.fields?.['Submitted At'] || a.fields?.['Created'] || '';
        const db = b.fields?.['Submitted At'] || b.fields?.['Created'] || '';
        return db.localeCompare(da);
      });
  }, [data]);

  const filtered = useMemo(() => {
    return intakes.filter(r => {
      const rs = r.fields?.['Review Status'] || 'New';
      const rl = r.fields?.['Form Language'] || 'en';
      if (filterStatus !== 'All' && rs !== filterStatus) return false;
      if (filterLang !== 'All' && rl !== filterLang) return false;
      const k = requestKindOf(r.fields || {});
      if (filterType !== 'All' && k.type !== filterType) return false;
      if (filterTopic !== 'All' && k.topic !== filterTopic) return false;
      return true;
    });
  }, [intakes, filterStatus, filterLang, filterType, filterTopic]);

  function openDetail(record) {
    setSelected(record);
    setNotes(record.fields?.['Review Notes'] || '');
    setSaveMsg(null);
    setDeleteError('');
  }

  /** Deletes the request only (with confirmation); the person, company, opportunity and first origin stay. */
  async function deleteRequest(record) {
    const rf = record.fields || {};
    const name = (rf['Company Context'] || '').split('—')[0].trim() || 'esta solicitud';
    const hasOpp = (data?.opportunities || []).some(o => [].concat(o.fields?.['Origin Request'] || []).includes(record.id));
    const msg = `¿Borrar la solicitud de ${name}?\n\nNo se puede deshacer desde Oike. No se borran el contacto, la empresa${hasOpp ? ', la oportunidad (que deja de mostrar de qué solicitud salió)' : ''} ni el primer origen de la persona; la solicitud deja de contarse en su enlace.`;
    if (!window.confirm(msg)) return;
    setDeleteError('');
    try {
      const res = await fetch('/api/intake-review', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ action: 'deleteRequest', id: record.id, confirm: true }),
      });
      if (!res.ok) throw new Error(res.status === 403 ? 'Solo la administración de Oike puede borrar solicitudes.' : 'No se pudo borrar. Vuelve a intentarlo.');
      if (onDeleteRecord) onDeleteRecord('diagnosticIntakes', record.id);
      setSelected(null);
    } catch (e) { setDeleteError(e.message); }
  }

  /** Saves the notes (and the status, if given). On failure nothing changes on screen and the text stays to retry. */
  async function saveNotes(record, newStatus) {
    setSaving(true);
    setSaveMsg(null);
    try {
      const body = { id: record.id, notes, status: newStatus || record.fields?.['Review Status'] || 'New' };
      const res = await fetch('/api/intake-review', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(res.status === 401 ? 'La sesión venció: volvé a entrar (el texto queda acá).'
          : res.status === 403 ? 'Solo la administración de Oike puede cambiar solicitudes.'
          : d.error || `Error ${res.status}`);
      }
      const fields = { 'Review Notes': notes, ...(newStatus ? { 'Review Status': newStatus } : {}) };
      const updated = { ...record, fields: { ...record.fields, ...fields } };
      setSelected(updated);
      if (onUpdateRecord) onUpdateRecord('diagnosticIntakes', record.id, fields);
      setSaveMsg({ tone: 'ok', kind: newStatus ? 'status' : 'notes', text: newStatus ? 'Estado guardado.' : 'Notas guardadas.' });
    } catch (e) {
      setSaveMsg({ tone: 'error', kind: newStatus ? 'status' : 'notes', text: `No se pudo guardar: ${e.message || 'sin conexión'}. ${newStatus ? 'El estado no cambió.' : 'Las notas siguen acá para reintentar.'}` });
    } finally {
      setSaving(false);
    }
  }

  const f = selected?.fields || {};

  return (
    <div style={{ padding: '24px', maxWidth: 1100 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--globant-text)', margin: 0 }}>Solicitudes nuevas</h1>
        <p style={{ color: 'var(--globant-muted)', marginTop: 4, fontSize: 14 }}>{loadError ? 'No se pudieron cargar las solicitudes' : `${intakes.length} solicitud${intakes.length !== 1 ? 'es' : ''} recibida${intakes.length !== 1 ? 's' : ''}`}</p>
      </div>

      <div className="tabs" role="tablist" style={{ marginBottom: 16 }}>
        <button type="button" role="tab" aria-selected={tab === 'requests'} className={`tab-btn ${tab === 'requests' ? 'active' : ''}`} onClick={() => setTab('requests')}>Solicitudes</button>
        <button type="button" role="tab" aria-selected={tab === 'links'} className={`tab-btn ${tab === 'links' ? 'active' : ''}`} onClick={() => setTab('links')}>Enlaces identificados</button>
      </div>

      {tab === 'links' && <RequestLinks links={data?.sources || []} intakes={intakes} stakeholders={data?.stakeholders || []} topics={topics} events={events} api={api} onAddRecord={onAddRecord} onUpdateRecord={onUpdateRecord} onDeleteRecord={onDeleteRecord} />}

      {tab === 'requests' && <>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div>
          <label style={{ fontSize: 12, color: 'var(--globant-muted)', marginRight: 6 }}>Estado</label>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            style={{ fontSize: 13, padding: '4px 8px', borderRadius: 6, border: '1px solid var(--globant-border)' }}>
            <option value="All">Todas</option>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{REQUEST_STATUS_ES[s] || s}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 12, color: 'var(--globant-muted)', marginRight: 6 }}>Idioma</label>
          <select value={filterLang} onChange={e => setFilterLang(e.target.value)}
            style={{ fontSize: 13, padding: '4px 8px', borderRadius: 6, border: '1px solid var(--globant-border)' }}>
            <option value="All">Todas</option>
            <option value="es">ES</option>
            <option value="en">EN</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: 12, color: 'var(--globant-muted)', marginRight: 6 }}>Tipo</label>
          <select value={filterType} onChange={e => setFilterType(e.target.value)}
            style={{ fontSize: 13, padding: '4px 8px', borderRadius: 6, border: '1px solid var(--globant-border)' }}>
            <option value="All">Todos</option>
            {Object.entries(FORM_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 12, color: 'var(--globant-muted)', marginRight: 6 }}>Tema</label>
          <select value={filterTopic} onChange={e => setFilterTopic(e.target.value)}
            style={{ fontSize: 13, padding: '4px 8px', borderRadius: 6, border: '1px solid var(--globant-border)' }}>
            <option value="All">Todos</option>
            {[...new Set(intakes.map(r => requestKindOf(r.fields || {}).topic).filter(Boolean))].map(c => <option key={c} value={c}>{topicName(c, topics)}</option>)}
          </select>
        </div>
      </div>

      {loadError && (
        <div style={{ marginBottom: 12 }}>
          <ErrorState message={`No se pudieron cargar las solicitudes (${loadError}).${intakes.length ? ' Se muestran las de la última carga.' : ''}`} onRetry={onReload} />
        </div>
      )}
      {loadError && !intakes.length ? null : filtered.length === 0 ? (
        <div style={{ color: '#56625F', fontSize: 15, padding: '40px 0', textAlign: 'center' }}>
          No hay solicitudes{filterStatus !== 'All' || filterLang !== 'All' || filterType !== 'All' || filterTopic !== 'All' ? ' con esos filtros' : ''}.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {filtered.map(r => {
            const rf = r.fields || {};
            const reviewStatus = rf['Review Status'] || 'New';
            const lang = rf['Form Language'] || 'en';
            const companyCtx = rf['Company Context'] || '';
            const companyName = companyCtx.split('—')[0]?.trim() || '—';
            return (
              <div key={r.id}
                onClick={() => openDetail(r)}
                style={{
                  background: 'var(--globant-card)', border: '1px solid var(--globant-border)', borderRadius: 10,
                  padding: '16px 20px', cursor: 'pointer', transition: 'box-shadow 0.15s',
                  boxShadow: selected?.id === r.id ? '0 0 0 2px var(--globant-green)' : 'none',
                }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = selected?.id === r.id ? '0 0 0 2px var(--globant-green)' : 'none'}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--globant-text)' }}>{companyName}</div>
                    <div style={{ fontSize: 13, color: 'var(--globant-muted)', marginTop: 2 }}>{rf['Main Challenge']?.slice(0, 100) || '—'}{rf['Main Challenge']?.length > 100 ? '…' : ''}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: '0 1 auto', minWidth: 0, flexWrap: 'wrap' }}>
                    {(() => { const iss = requestIssuesOf(rf, { links: data?.sources || [], events }); return iss.length ? <span style={{ fontSize: 11, borderRadius: 4, padding: '2px 7px', background: '#FBEAE7', color: '#9B2C20', fontWeight: 600 }} title="Se guardó, pero no quedó registrado todo lo que pedía su enlace">Revisar: falta {iss.map(i => ISSUE_LABELS[i]).join(', ')}</span> : null; })()}
                    {(() => { const k = requestKindOf(rf); return <span style={{ fontSize: 11, borderRadius: 4, padding: '2px 7px', background: '#F1EFEA', color: 'var(--globant-muted)' }}>{FORM_TYPES[k.type].label}{k.topic ? ` · ${topicName(k.topic, topics)}` : ''}</span>; })()}
                    <OriginTag request={r} />
                    <span style={{ fontSize: 11, background: '#F1EFEA', borderRadius: 4, padding: '2px 7px', color: 'var(--globant-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{lang}</span>
                    <span style={{
                      fontSize: 12, borderRadius: 20, padding: '3px 10px', fontWeight: 600,
                      background: (STATUS_COLORS[reviewStatus] || '#6b7280') + '18',
                      color: STATUS_COLORS[reviewStatus] || '#6b7280',
                    }}>{REQUEST_STATUS_ES[reviewStatus] || reviewStatus}</span>
                    <span style={{ fontSize: 12, color: '#56625F' }}>{formatDate(rf['Submitted At'] || rf['Created'])}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      </>}

      {/* Detail panel */}
      {selected && (
        <div style={{
          position: 'fixed', top: 0, right: 0, width: 520, maxWidth: '100vw', height: '100vh',
          background: 'var(--globant-card)', borderLeft: '1px solid var(--globant-border)', overflowY: 'auto',
          zIndex: 200, boxShadow: '-4px 0 24px rgba(0,0,0,0.10)', padding: '28px 28px 40px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--globant-text)' }}>
              {f['Company Context']?.split('—')[0]?.trim() || 'Solicitud'}
            </h3>
            <button onClick={() => setSelected(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#56625F', lineHeight: 1 }}>×</button>
          </div>

          {/* Status badge + change */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 20 }}>
            <select
              value={f['Review Status'] || 'New'}
              disabled={saving}
              onChange={e => saveNotes(selected, e.target.value)}
              style={{
                fontSize: 13, fontWeight: 600, padding: '5px 10px', borderRadius: 20,
                border: `2px solid ${STATUS_COLORS[f['Review Status'] || 'New'] || '#6b7280'}`,
                color: STATUS_COLORS[f['Review Status'] || 'New'] || '#6b7280',
                background: (STATUS_COLORS[f['Review Status'] || 'New'] || '#6b7280') + '12',
                cursor: 'pointer',
              }}>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{REQUEST_STATUS_ES[s] || s}</option>)}
            </select>
            <span style={{ fontSize: 12, color: '#56625F' }}>
              {formatDate(f['Submitted At'] || f['Created'])} · {f['Form Language'] === 'es' ? 'Español' : 'English'}
            </span>
          </div>
          {saveMsg?.kind === 'status' && (
            <div role={saveMsg.tone === 'error' ? 'alert' : 'status'} style={{ fontSize: 12, margin: '-12px 0 16px', color: saveMsg.tone === 'error' ? '#B4362C' : '#1E7B4F' }}>{saveMsg.text}</div>
          )}

          <RequestOriginPanel key={`o-${selected.id}`} request={selected} links={data?.sources || []}
            onSaved={(fields, personFields) => {
              selected.fields = { ...selected.fields, ...fields };
              setSelected({ ...selected });
              if (onUpdateRecord) onUpdateRecord('diagnosticIntakes', selected.id, fields);
              const stkId = (selected.fields['Stakeholder'] || [])[0];
              if (personFields && stkId && onUpdateRecord) onUpdateRecord('stakeholders', stkId, personFields);
            }} />
          <RequestPersonPanel request={selected} stakeholders={data?.stakeholders || []} intakes={intakes} />
          <RequestOpportunityPanel request={selected} opportunities={data?.opportunities || []} api={api} onAddRecord={onAddRecord} />

          {(() => {
            const iss = requestIssuesOf(f, { links: data?.sources || [], events });
            if (!iss.length) return null;
            const why = {
              type: 'El tipo no se guardó: probablemente faltan los campos "Request Type", "Topic Code" o "Request Event" en Diagnostic Intake.',
              topic: 'El tema no se guardó: no está en la lista activa de temas (o falta la tabla "Request Topics"), o faltan los campos del tipo.',
              event: 'No quedó a qué webinar se inscribió: el enlace no tiene evento ("Link Event"), se envió sin enlace o faltan los campos del tipo.',
              invitation: 'La invitación al evento no quedó registrada: revisá el campo "Stakeholders invited" del evento.',
            };
            return (
              <div role="alert" style={{ background: '#FBEAE7', border: '1px solid #F0C4BC', color: '#7A2318', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 13, lineHeight: 1.5 }}>
                <b>La solicitud se guardó incompleta.</b> Falta {iss.map(i => ISSUE_LABELS[i]).join(', ')}. A la persona se le avisó que lo vas a confirmar.
                <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>{iss.map(i => <li key={i}>{why[i]}</li>)}</ul>
              </div>
            );
          })()}
          {(() => {
            const k = requestKindOf(f);
            const ev = k.eventId ? events.find(e => e.id === k.eventId) : null;
            return (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline', margin: '0 0 14px', fontSize: 13 }}>
                <b>{FORM_TYPES[k.type].label}</b>
                {k.topic && <span>· Tema: {topicName(k.topic, topics)}</span>}
                {k.type === 'webinar' && <span>· {ev ? `Invitación al evento: ${ev.fields?.['Event Name'] || 'Evento'}` : 'Sin evento vinculado'}</span>}
              </div>
            );
          })()}
          {requestKindOf(f).type === 'team' ? <>
            <IntakeField label="Contexto de la empresa" value={f['Company Context']} />
            <IntakeField label="Tamaño del equipo comercial" value={f['Sales Team Size']} />
            <IntakeField label="Cómo venden hoy" value={f['Current Sales Motion']} />
            <IntakeField label="Desafío principal" value={f['Main Challenge']} />
            <IntakeField label="Objetivos" value={f['Goals']} />
          </> : requestKindOf(f).type === 'individual' ? <>
            <IntakeField label="Quién es" value={f['Company Context']} />
            <IntakeField label="En qué momento está" value={f['Main Challenge']} />
            <IntakeField label="Qué le gustaría lograr" value={f['Goals']} />
          </> : <>
            <IntakeField label="Quién es" value={f['Company Context']} />
            <IntakeField label="Qué le gustaría que se trate" value={f['Additional Context'] || '—'} />
          </>}
          {f["What They've Tried"] && <IntakeField label="Qué probaron" value={f["What They've Tried"]} />}
          {f['Pipeline Last 90 Days'] && <IntakeField label="Pipeline de los últimos 90 días" value={f['Pipeline Last 90 Days']} />}
          {f['Prospecting Activity'] && <IntakeField label="Actividad de prospección" value={f['Prospecting Activity']} />}
          {f['Messaging Examples'] && <IntakeField label="Ejemplos de mensajes" value={f['Messaging Examples']} />}
          {f['Additional Context'] && requestKindOf(f).type !== 'webinar' && <IntakeField label="Contexto adicional" value={f['Additional Context']} />}

          {/* Notas de revisión */}
          <div style={{ marginTop: 24 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--globant-muted)', display: 'block', marginBottom: 6 }}>Notas de revisión</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={4}
              placeholder="Notas internas sobre esta solicitud..."
              style={{
                width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--globant-border)',
                fontSize: 13, resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box',
              }}
            />
            <button
              type="button"
              className="action-btn btn-primary"
              onClick={() => saveNotes(selected)}
              disabled={saving}
              style={{ marginTop: 8, fontSize: 13 }}>
              {saving ? 'Guardando…' : saveMsg?.tone === 'error' ? 'Reintentar' : 'Guardar notas'}
            </button>
            {saveMsg?.kind === 'notes' && (
              <div role={saveMsg.tone === 'error' ? 'alert' : 'status'} style={{ fontSize: 12, marginTop: 6, color: saveMsg.tone === 'error' ? '#B4362C' : '#1E7B4F' }}>{saveMsg.text}</div>
            )}
          </div>

          {CURRENT_USER?.role !== 'bdr' && (
            <div style={{ marginTop: 28, paddingTop: 16, borderTop: '1px solid var(--globant-border)' }}>
              <button type="button" className="action-btn btn-danger" style={{ fontSize: 12 }} onClick={() => deleteRequest(selected)}>Borrar solicitud</button>
              <div style={{ fontSize: 12, color: '#56625F', marginTop: 6 }}>Borra solo la solicitud. El contacto, la empresa, la oportunidad y el primer origen de la persona se conservan.</div>
              {deleteError && <div role="alert" style={{ fontSize: 12, color: '#B4362C', marginTop: 6 }}>{deleteError}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function IntakeField({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#56625F', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13, color: '#334155', whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{value}</div>
    </div>
  );
}
